<template>
  <div class="figure-recipes">
    <div class="fr-heading">
      <h6 class="mb-0"><i class="fas fa-magic"></i> Quick recipes</h6>
      <small class="text-muted">One-click presets that pick sensible defaults for you.</small>
    </div>

    <div class="fr-grid">
      <a
        v-for="recipe in applicableRecipes"
        :key="recipe.id"
        class="fr-card"
        :class="{ disabled: !canApply(recipe) }"
        @click="canApply(recipe) && apply(recipe)"
      >
        <div class="fr-card-icon"><i :class="recipe.icon"></i></div>
        <div class="fr-card-body">
          <div class="fr-card-title">{{ recipe.label }}</div>
          <div class="fr-card-desc">{{ recipe.description }}</div>
        </div>
      </a>
    </div>

    <div v-if="!applicableRecipes.length" class="text-muted small mt-2">
      No recipes apply to this dataset. Try selecting another dataset or
      configure a chart manually below.
    </div>
  </div>
</template>

<script>
/* eslint-disable no-console */
import { numericColumns, allColumns, columnType } from '../services/ChartService.js'

/**
 * A Recipe is a preset: {figureType, defaultConfig, appliesWhen(dataset)}.
 * `appliesWhen` returns the auto-filled config or null if not applicable.
 */
const RECIPES = [
  {
    id: 'distribution',
    label: 'Distribution of a value',
    icon: 'fas fa-chart-area',
    description: 'Histogram + density curve + summary statistics. Great for understanding any numeric field.',
    figureTypeId: 'histogram',
    appliesWhen (ds) {
      const num = numericColumns(ds)
      if (!num.length) return null
      // Prefer a column called "value" if it exists (our analysis output)
      const pick = num.includes('value') ? 'value' : num[0]
      return {
        xAxis: pick,
        title: `Distribution of ${pick}`,
        xLabel: pick,
        yLabel: 'Count',
        showDensity: true,
        showGrid: true,
        showLegend: true
      }
    }
  },
  {
    id: 'scatter_trend',
    label: 'Scatter with trendline',
    icon: 'fas fa-braille',
    description: 'Plot two numeric fields against each other with a linear regression overlay (slope, intercept, R²).',
    figureTypeId: 'scatter',
    appliesWhen (ds) {
      const num = numericColumns(ds)
      if (num.length < 2) return null
      return {
        xAxis: num[0],
        yAxis: num[1],
        title: `${num[1]} vs. ${num[0]}`,
        xLabel: num[0],
        yLabel: num[1],
        showRegression: true,
        pointSize: 4,
        showGrid: true
      }
    }
  },
  {
    id: 'time_series',
    label: 'Value over time',
    icon: 'far fa-calendar-alt',
    description: 'If your data has a date field, plot a numeric value as a trend line over time.',
    figureTypeId: 'timeseries',
    appliesWhen (ds) {
      const num = numericColumns(ds)
      const all = allColumns(ds)
      const dateField = all.find(f => columnType(ds, f) === 'date')
      if (!dateField || !num.length) return null
      const pick = num.includes('value') ? 'value' : num[0]
      return {
        xAxis: dateField,
        yAxis: pick,
        title: `${pick} over time`,
        xLabel: 'Date',
        yLabel: pick,
        showPoints: true,
        fillArea: false,
        tension: 0.2,
        showGrid: true
      }
    }
  },
  {
    id: 'compare_categories',
    label: 'Compare categories',
    icon: 'fas fa-chart-bar',
    description: 'Bar chart — mean of a numeric value grouped by a category field.',
    figureTypeId: 'bar',
    appliesWhen (ds) {
      const all = allColumns(ds)
      const num = numericColumns(ds)
      const cat = all.find(f => {
        if (num.includes(f)) return false
        const t = columnType(ds, f)
        return t === 'text' || t === 'number' // allow numeric categories too
      })
      if (!cat || !num.length) return null
      return {
        xAxis: cat,
        yAxis: num[0],
        aggregation: 'mean',
        title: `Mean ${num[0]} by ${cat}`,
        xLabel: cat,
        yLabel: `Mean ${num[0]}`,
        showGrid: true
      }
    }
  },
  {
    id: 'box_by_group',
    label: 'Spread per group',
    icon: 'fas fa-grip-lines',
    description: 'Box-and-whisker plot — quartiles, median, and outliers for each category.',
    figureTypeId: 'box',
    appliesWhen (ds) {
      const all = allColumns(ds)
      const num = numericColumns(ds)
      const cat = all.find(f => columnType(ds, f) === 'text' && !num.includes(f))
      if (!num.length) return null
      return {
        yAxis: num[0],
        xAxis: cat || '',
        title: cat ? `${num[0]} by ${cat}` : `${num[0]} distribution`,
        xLabel: cat || '',
        yLabel: num[0],
        showGrid: true
      }
    }
  },
  {
    id: 'correlation_matrix',
    label: 'Correlation matrix',
    icon: 'fas fa-th',
    description: 'Pearson correlation across all numeric fields — spot which fields move together.',
    figureTypeId: 'correlation',
    appliesWhen (ds) {
      const num = numericColumns(ds)
      if (num.length < 3) return null
      return {
        columns: num.slice(0, 8),
        title: 'Correlation matrix',
        showGrid: true
      }
    }
  },
  {
    id: 'category_share',
    label: 'Share by category',
    icon: 'fas fa-chart-pie',
    description: 'Donut chart showing the proportion of each unique value in a category field.',
    figureTypeId: 'pie',
    appliesWhen (ds) {
      const all = allColumns(ds)
      const cat = all.find(f => columnType(ds, f) === 'text')
      if (!cat) return null
      return {
        xAxis: cat,
        title: `Share by ${cat}`,
        donut: true,
        showLegend: true
      }
    }
  }
]

export default {
  name: 'FigureRecipes',
  props: {
    dataset: { type: Object, default: null }
  },
  computed: {
    applicableRecipes () {
      if (!this.dataset) return RECIPES
      return RECIPES.map(r => ({
        ...r,
        _config: r.appliesWhen(this.dataset)
      })).filter(r => r._config)
    }
  },
  methods: {
    canApply (recipe) {
      return !!recipe._config
    },
    apply (recipe) {
      this.$emit('apply', {
        figureTypeId: recipe.figureTypeId,
        config: recipe._config,
        recipeId: recipe.id,
        recipeLabel: recipe.label
      })
    }
  }
}
</script>

<style scoped>
.figure-recipes { margin-bottom: 16px; }
.fr-heading {
  margin-bottom: 10px;
}
.fr-heading h6 {
  font-size: 0.85rem;
  font-weight: 600;
  color: #0a4da8;
  margin-bottom: 2px;
}
.fr-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}
.fr-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #e5e9f0;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  transition: all 0.12s;
  text-decoration: none;
  color: inherit;
}
.fr-card:hover {
  border-color: #0b5ed7;
  background: #f8fbff;
  box-shadow: 0 1px 4px rgba(11,94,215,0.12);
  text-decoration: none;
  color: inherit;
}
.fr-card.disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.fr-card.disabled:hover {
  border-color: #e5e9f0;
  background: #fff;
  box-shadow: none;
}
.fr-card-icon {
  font-size: 1.1rem;
  color: #0b5ed7;
  width: 24px;
  flex-shrink: 0;
  text-align: center;
}
.fr-card-body { flex: 1; }
.fr-card-title {
  font-weight: 600;
  font-size: 0.86rem;
  color: #212529;
  line-height: 1.25;
}
.fr-card-desc {
  font-size: 0.76rem;
  color: #6c757d;
  line-height: 1.35;
  margin-top: 2px;
}
</style>
